/**
 * Cuándo el Copiloto corresponde a lo que el asesor tiene delante (DEF-029).
 *
 * QA de TEST, 02-oct-2026: el Copiloto se pedía al abrir la conversación y con
 * «Reintentar», nada más, mientras el hilo se refresca cada 2,5 s. La
 * intención seguía en «Confirmar si faltan documentos» con el cliente pidiendo
 * un asesor, y tras «Empezar de nuevo» el resumen describía el expediente
 * ANTERIOR —otro solicitante— con aplomo y con un borrador listo para enviar.
 *
 * El backend arma con UNA función (`_base_del_copiloto`) el estado del caso
 * que el detalle muestra (`copiloto_base`) y el que el Copiloto resumió
 * (`base`). Acá solo se comparan:
 *
 *   vigente          mismo expediente, mismo checklist, mismo último mensaje
 *   desactualizado   mismo expediente, pero pasó algo después: se muestra
 *                    marcado «Actualizando…» y se vuelve a pedir
 *   otro_expediente  el resumen es de un expediente que ya no está: se
 *                    descarta, no se muestra ni un instante
 *   sin_base         falta una de las dos (todavía no hay detalle, o la
 *                    respuesta fue un error): se muestra lo que haya
 *
 * Puras y sin más import que un tipo, para que el test las corra en Node sobre
 * lo que devuelve el backend (`tests/unit/portal_ts.py`).
 */
import type { BaseDelCopiloto } from './types/portal'

export type EstadoDelCopiloto = 'vigente' | 'desactualizado' | 'otro_expediente' | 'sin_base'

/**
 * Cuánto se espera desde el último cambio antes de volver a pedirlo. Una
 * ráfaga del cliente son varios mensajes en segundos: pedirlo con cada uno
 * serían varias llamadas a Bedrock para quedarse con la última.
 */
export const ESPERA_MS = 4_000

/** Una clave estable para comparar dos estados, o '' sin estado. */
export function claveDeBase(base?: BaseDelCopiloto | null): string {
  if (!base) return ''
  return [base.expediente, base.checklist_generated_at, base.ultimo_mensaje].join('|')
}

export function estadoDelCopiloto(
  actual?: BaseDelCopiloto | null,
  resumida?: BaseDelCopiloto | null,
): EstadoDelCopiloto {
  if (!actual || !resumida) return 'sin_base'
  if (actual.expediente !== resumida.expediente) return 'otro_expediente'
  return claveDeBase(actual) === claveDeBase(resumida) ? 'vigente' : 'desactualizado'
}

/**
 * ¿Hay que volver a pedirlo ahora?
 *
 * Solo con la pestaña del Copiloto a la vista —nadie lo está leyendo si no—,
 * sin otro pedido en vuelo, y si el estado que hay delante no es el mismo con
 * el que ya se pidió. Esa última condición es la que evita el bucle: si por un
 * desfase las dos bases no llegan a coincidir nunca, se pide una vez por cada
 * cambio del caso, no cada cuatro segundos.
 */
export function debePedir(p: {
  visible: boolean
  enVuelo: boolean
  estado: EstadoDelCopiloto
  claveActual: string
  pedidoCon: string | null
}): boolean {
  if (!p.visible || p.enVuelo || !p.claveActual) return false
  if (p.estado === 'vigente') return false
  return p.claveActual !== p.pedidoCon
}

/** Cuánto falta para pedirlo, contando desde el último cambio del caso. Si el
 *  asesor vuelve a la pestaña mucho después, no espera nada. */
export function esperaRestante(ahora: number, cambioEn: number, espera = ESPERA_MS): number {
  return Math.max(0, espera - (ahora - cambioEn))
}
